import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Pagination,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import HistoryIcon from "@mui/icons-material/History";
import { parseEmails } from "../../../utils/parse-emails";
import EmailTemplatesEditor from "../../../components/EmailTemplatesEditor";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Accordion, AccordionDetails, AccordionSummary, Link } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "../new-league-view/styles.css";
import { useNavigate, useNavigationType, useParams, useSearchParams } from "react-router-dom";
import { UnsavedChangesProvider, useReportUnsaved } from "../../../components/unsaved-changes";
import { getLeague, getRinks, updateLeague, createRink, getLeagueGames, getNeighboringCities, addNeighboringCity, removeNeighboringCity, deleteRink, reassignAndRemoveRink, getMyLeagues, getAllowedMembers, addAllowedMember, bulkAddAllowedMembers, bulkRemoveAllowedMembers, getLeaguePriceHistory } from "../../../services/api/leagues";
import { updateGame, cancelGame, deleteGame } from "../../../services/api/games";
import { getLeaguePayments, updatePaymentStatus } from "../../../services/api/payments";
import { fetchMySubscription } from "../../../services/api/subscriptions";
import { PLAN_DEFINITIONS } from "../../../config/plans";
import { getLeagueWaiversAdmin, createWaiver, setWaiverActive, updateWaiverContent, getWaiverSignatures } from "../../../services/api/waivers";
import CityStateAutocomplete from "../../../components/CityStateAutocomplete";
import StateSelect from "../../../components/StateSelect";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import PhoneTextField from "../../../components/PhoneTextField";
import RichTextEditor from "../../../components/RichTextEditor";
import { STANDARD_HOCKEY_WAIVER_NAME, buildStandardHockeyWaiverHtml } from "../../../constants/hockey-waiver-template";
import { loadErrorMessage } from "../../../utils/api-error";

const ORGANIZER_ROLES = new Set(["owner", "admin", "treasurer", "coach"]);

// URL slug for each tab, in tab order: /manage-league/:leagueId/:slug
const MANAGE_TAB_SLUGS = ["games", "waivers", "payments", "settings", "notifications"];

// Games list paging: allowed ?perPage= values ("all" shows every game on one page).
const GAME_PAGE_SIZES = ["5", "10", "25", "all"];
const DEFAULT_GAME_PAGE_SIZE = "5";
const gamesScrollKey = (leagueId) => `manage-league-games-scroll:${leagueId}`;

// The league settings the Settings tab edits and "Save" persists. Compared with what was last loaded or saved
// to tell whether anything is unsaved.
const SETTINGS_FIELDS = [
  "leagueName", "leagueType", "skillLevel", "selectedPlan", "payoutMethod", "venmoHandle", "city", "state",
  "playerCostDollars", "goalieCostDollars", "maxSkaterSpots", "maxGoalieSpots", "gamePaymentType",
  "iceTimeCostDollars", "visibility", "useAllowedList",
];
const settingsSnapshot = (form) => JSON.stringify(SETTINGS_FIELDS.map((key) => form[key]));

/** The form's field values for a league as loaded from the server (exactly what the form shows). */
const leagueToForm = (leagueData) => ({
  leagueName: leagueData.name ?? "",
  leagueType: leagueData.leagueType ?? "PICKUP",
  skillLevel: leagueData.skillLevel ?? "ALL_WELCOME",
  selectedPlan: leagueData.planId ?? "launch",
  payoutMethod: leagueData.payoutMethod ?? "stripe",
  venmoHandle: (leagueData.venmoHandle ?? "").replace(/^@+/, ""),
  city: leagueData.city ?? "",
  state: leagueData.state ?? "",
  playerCostDollars: leagueData.playerCost != null ? (leagueData.playerCost / 100).toString() : "",
  goalieCostDollars: leagueData.goalieCost != null ? (leagueData.goalieCost / 100).toString() : "0",
  maxSkaterSpots: leagueData.maxSkaterSpots != null ? String(leagueData.maxSkaterSpots) : "",
  maxGoalieSpots: leagueData.maxGoalieSpots != null ? String(leagueData.maxGoalieSpots) : "",
  gamePaymentType: leagueData.gamePaymentType ?? "DROP_IN",
  iceTimeCostDollars: leagueData.iceTimeCost != null ? (leagueData.iceTimeCost / 100).toString() : "",
  visibility: leagueData.visibility ?? "PUBLIC",
  useAllowedList: leagueData.useAllowedList ?? false,
});

// Text with the formatting stripped, so a waiver is compared by what it says (the rich text editor rewrites the
// HTML the moment it opens, which would otherwise look like an edit).
const plainText = (html) => (html ?? "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

/** Warns before leaving the page (or a tab) with unsaved changes. See components/unsaved-changes.jsx. */
export default function ManageLeagueView() {
  const { leagueId } = useParams();
  return (
    <UnsavedChangesProvider pagePath={`/manage-league/${leagueId}`}>
      <ManageLeagueViewContent />
    </UnsavedChangesProvider>
  );
}

function ManageLeagueViewContent() {
  const theme = useTheme();
  // Five tabs don't fit across a phone, so on small screens the tab bar scrolls within the page width.
  const tabsScroll = useMediaQuery(theme.breakpoints.down("sm"));
  const { leagueId, tab: tabSlug } = useParams();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [league, setLeague] = React.useState(null);
  const [existingRinks, setExistingRinks] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [forbidden, setForbidden] = React.useState(false);

  const [selectedPlan, setSelectedPlan] = React.useState("launch");
  const [payoutMethod, setPayoutMethod] = React.useState("stripe");
  const [savedPayoutMethod, setSavedPayoutMethod] = React.useState("stripe");
  // The settings as last loaded or saved. null until the league has loaded, so nothing is "unsaved" before that.
  const [settingsBaseline, setSettingsBaseline] = React.useState(null);
  const [userSubscription, setUserSubscription] = React.useState(null);
  const isLaunchTier = !userSubscription || userSubscription?.planId === "launch";
  const activePlanDef = PLAN_DEFINITIONS.find((p) => p.id === (userSubscription?.planId ?? "launch")) ?? PLAN_DEFINITIONS[0];
  const participantLimit = activePlanDef.participantLimit;
  // Until the subscription loads we don't know the real limit, so don't flag anything.
  const capacityLimit = userSubscription ? participantLimit : Infinity;
  const [plannedGameCount, setPlannedGameCount] = React.useState("");
  const initialCapacityRef = React.useRef(0);
  const rinkLimit = isLaunchTier ? 1 : Infinity;
  const [leagueName, setLeagueName] = React.useState("");
  const [leagueType, setLeagueType] = React.useState("PICKUP");
  const [skillLevel, setSkillLevel] = React.useState("ALL_WELCOME");
  const [venmoHandle, setVenmoHandle] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [playerCostDollars, setPlayerCostDollars] = React.useState("");
  const [goalieCostDollars, setGoalieCostDollars] = React.useState("0");
  const [maxSkaterSpots, setMaxSkaterSpots] = React.useState("");
  const [maxGoalieSpots, setMaxGoalieSpots] = React.useState("");
  const [gamePaymentType, setGamePaymentType] = React.useState("DROP_IN");
  const [iceTimeCostDollars, setIceTimeCostDollars] = React.useState("");
  const [saveState, setSaveState] = React.useState({ loading: false, message: "", error: "" });
  const [leagueGames, setLeagueGames] = React.useState([]);
  // The games list's page and page size live in the URL (?page=3&perPage=10) rather than in component state,
  // so coming back from a roster with the browser's Back button lands on the same page.
  const [searchParams, setSearchParams] = useSearchParams();
  const gamePage = Math.max(1, parseInt(searchParams.get("page") ?? "", 10) || 1);
  const gamePageSizeSetting = GAME_PAGE_SIZES.includes(searchParams.get("perPage")) ? searchParams.get("perPage") : DEFAULT_GAME_PAGE_SIZE;
  const setGamesView = (page, perPage) => {
    const next = new URLSearchParams(searchParams);
    if (page > 1) next.set("page", String(page));
    else next.delete("page");
    if (perPage !== DEFAULT_GAME_PAGE_SIZE) next.set("perPage", perPage);
    else next.delete("perPage");
    // Replace, so Back leaves the league instead of stepping through every page flipped.
    setSearchParams(next, { replace: true });
  };
  const [addRinkLoading, setAddRinkLoading] = React.useState(false);
  const [rinkName, setRinkName] = React.useState("");
  const [rinkAddress, setRinkAddress] = React.useState("");
  const [rinkPhone, setRinkPhone] = React.useState("");
  const [addRinkError, setAddRinkError] = React.useState("");
  const [neighboringCities, setNeighboringCities] = React.useState([]);
  const [pendingNeighboringCities, setPendingNeighboringCities] = React.useState([]);
  const [neighboringCity, setNeighboringCity] = React.useState("");
  const [neighboringCityState, setNeighboringCityState] = React.useState("");
  const [addCityError, setAddCityError] = React.useState("");
  const [rinkConflictDialog, setRinkConflictDialog] = React.useState({
    open: false, rinkId: "", rinkName: "", affectedGames: [], replacementRinkId: "",
  });
  const [rinkDeleteError, setRinkDeleteError] = React.useState("");
  const [pendingRinkRemovals, setPendingRinkRemovals] = React.useState([]);
  const [showOnlyRinkWarning, setShowOnlyRinkWarning] = React.useState(false);

  // Waiver management state
  const [waivers, setWaivers] = React.useState([]);
  const [waiverDialogOpen, setWaiverDialogOpen] = React.useState(false);
  const [editingWaiver, setEditingWaiver] = React.useState(null);
  const [waiverName, setWaiverName] = React.useState("");
  const [waiverHtml, setWaiverHtml] = React.useState("");
  const [waiverSaving, setWaiverSaving] = React.useState(false);
  const [waiverError, setWaiverError] = React.useState("");
  // Signature list state: { [waiverId]: { loading, rows } }
  const [signaturesMap, setSignaturesMap] = React.useState({});

  // Edit game dialog state
  const [editGameDialog, setEditGameDialog] = React.useState({ open: false, game: null });
  const [editRinkId, setEditRinkId] = React.useState("");
  const [editStartsAt, setEditStartsAt] = React.useState(null);
  const [editDurationMinutes, setEditDurationMinutes] = React.useState("");
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState("");

  // Cancel / delete game dialog state
  const [cancelGameDialog, setCancelGameDialog] = React.useState({ open: false, game: null });
  const [cancelGameLoading, setCancelGameLoading] = React.useState(false);
  const [cancelGameError, setCancelGameError] = React.useState("");
  const [deleteGameDialog, setDeleteGameDialog] = React.useState({ open: false, game: null });
  const [deleteGameLoading, setDeleteGameLoading] = React.useState(false);
  const [deleteGameError, setDeleteGameError] = React.useState("");

  // Bulk game action state
  const [selectedGameIds, setSelectedGameIds] = React.useState([]);
  const [actionsMenuAnchor, setActionsMenuAnchor] = React.useState(null);
  const [bulkEditDialog, setBulkEditDialog] = React.useState({ open: false });
  const [bulkEditRinkId, setBulkEditRinkId] = React.useState("__unchanged__");
  const [bulkEditStartsAt, setBulkEditStartsAt] = React.useState(null);
  const [bulkEditDurationMinutes, setBulkEditDurationMinutes] = React.useState("");
  const [bulkEditSaving, setBulkEditSaving] = React.useState(false);
  const [bulkEditError, setBulkEditError] = React.useState("");
  const [bulkEditFailures, setBulkEditFailures] = React.useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = React.useState({ open: false });
  const [bulkDeleteLoading, setBulkDeleteLoading] = React.useState(false);
  const [bulkDeleteError, setBulkDeleteError] = React.useState("");
  // The tab lives in the URL (/manage-league/:leagueId/:tab) so a refresh or shared link keeps its place.
  const activeTab = Math.max(0, MANAGE_TAB_SLUGS.indexOf(tabSlug));
  const setActiveTab = (index) => navigate(`/manage-league/${leagueId}/${MANAGE_TAB_SLUGS[index]}`);
  const unknownTabSlug = tabSlug !== undefined && !MANAGE_TAB_SLUGS.includes(tabSlug);

  // Coming back from a roster: scroll to where the organizer was. Waits until the games have loaded, because
  // before then the page is too short to scroll that far.
  const gamesLoadedCount = leagueGames.length;
  React.useEffect(() => {
    if (activeTab !== 0 || gamesLoadedCount === 0 || !leagueId) return;
    const saved = sessionStorage.getItem(gamesScrollKey(leagueId));
    if (saved === null) return;
    sessionStorage.removeItem(gamesScrollKey(leagueId));
    // Only on Back/Forward (or a reload). Arriving some other way, e.g. a fresh link, starts at the top.
    if (navigationType !== "POP") return;
    const y = Number(saved);
    if (Number.isFinite(y)) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [activeTab, gamesLoadedCount, leagueId, navigationType]);
  React.useEffect(() => {
    if (unknownTabSlug) navigate(`/manage-league/${leagueId}/${MANAGE_TAB_SLUGS[0]}`, { replace: true });
  }, [unknownTabSlug, leagueId, navigate]);

  // Payments tab state
  const [leaguePayments, setLeaguePayments] = React.useState([]);
  // Existing payments block switching this league to organizer-covered ("none").
  const organizerCoveredBlocked =
    savedPayoutMethod !== "none" && Array.isArray(leaguePayments) && leaguePayments.length > 0;
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [paymentsError, setPaymentsError] = React.useState("");
  const [togglingPayment, setTogglingPayment] = React.useState({});
  const [paymentsFilter, setPaymentsFilter] = React.useState("pending");
  const [confirmUnpaid, setConfirmUnpaid] = React.useState({ open: false, paymentId: null });
  const [expandedPaymentUser, setExpandedPaymentUser] = React.useState(null);
  const [confirmMarkAll, setConfirmMarkAll] = React.useState({ open: false, name: "", payments: [] });

  // Visibility & allowed list
  const [visibility, setVisibility] = React.useState("PUBLIC");
  const [useAllowedList, setUseAllowedList] = React.useState(false);
  const [allowedMembers, setAllowedMembers] = React.useState([]);
  const [allowedMemberEmail, setAllowedMemberEmail] = React.useState("");
  const [allowedMemberLoading, setAllowedMemberLoading] = React.useState(false);
  const [allowedMemberError, setAllowedMemberError] = React.useState("");
  const [bulkText, setBulkText] = React.useState("");
  const [bulkDragging, setBulkDragging] = React.useState(false);
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [bulkResult, setBulkResult] = React.useState("");
  const bulkFileInputRef = React.useRef(null);
  const [selectedAllowedIds, setSelectedAllowedIds] = React.useState([]);
  const [removingSelected, setRemovingSelected] = React.useState(false);

  // Price override & history
  const [priceOverrideEnabled, setPriceOverrideEnabled] = React.useState(false);
  const pricingLocked =
    leagueGames.some((g) => (g.skaterSpotsFilled ?? 0) > 0 || (g.goalieSpotsFilled ?? 0) > 0) &&
    !priceOverrideEnabled;

  // Suggests a player cost from the ice rental cost. Only called from user edits (never on load),
  // and never while pricing is locked, so an existing league's saved price isn't overwritten.
  const recalcPlayerCost = (iceCost, skaterSpots, gameCount, paymentType) => {
    if (pricingLocked || payoutMethod === "none") return;
    const cost = parseFloat(iceCost);
    const spots = parseInt(skaterSpots, 10);
    if (isNaN(cost) || isNaN(spots) || spots <= 0) return;
    if (paymentType === "FULL_LEAGUE") {
      setPlayerCostDollars((cost / spots).toFixed(2));
    } else {
      const games = parseInt(gameCount, 10);
      if (!isNaN(games) && games > 0) {
        setPlayerCostDollars((cost / spots / games).toFixed(2));
      }
    }
  };
  const [priceOverrideDialogOpen, setPriceOverrideDialogOpen] = React.useState(false);
  const [priceOverrideAcknowledged, setPriceOverrideAcknowledged] = React.useState(false);
  const [priceHistoryDialogOpen, setPriceHistoryDialogOpen] = React.useState(false);
  const [priceHistory, setPriceHistory] = React.useState([]);
  const [priceHistoryLoading, setPriceHistoryLoading] = React.useState(false);

  // ── Unsaved changes ───────────────────────────────────────────────────────
  // Everything the Settings "Save" button would persist, including rink removals and neighbouring cities that
  // are staged until then.
  const currentSettings = {
    leagueName, leagueType, skillLevel, selectedPlan, payoutMethod, venmoHandle, city, state, playerCostDollars,
    goalieCostDollars, maxSkaterSpots, maxGoalieSpots, gamePaymentType, iceTimeCostDollars, visibility, useAllowedList,
  };
  const settingsDirty =
    (settingsBaseline !== null && settingsSnapshot(currentSettings) !== settingsSnapshot(settingsBaseline)) ||
    pendingRinkRemovals.length > 0 ||
    pendingNeighboringCities.length > 0;
  useReportUnsaved("settings", settingsDirty, { label: "League settings" });

  // Text typed into an "add" box that hasn't been added. Adding a rink or emails saves immediately, so only the
  // typed-but-not-added text is at risk.
  const unaddedInput = [rinkName, rinkAddress, rinkPhone, neighboringCity, neighboringCityState, allowedMemberEmail, bulkText].some(
    (v) => v.trim() !== "",
  );
  useReportUnsaved("unadded-input", unaddedInput, { label: "Text you entered but haven't added yet (rink, city or email)" });

  // A waiver being written or edited in its dialog.
  const waiverDirty =
    waiverDialogOpen &&
    (editingWaiver
      ? waiverName !== (editingWaiver.name ?? "") || plainText(waiverHtml) !== plainText(editingWaiver.contentHtml)
      : waiverName.trim() !== "" || plainText(waiverHtml) !== "");
  useReportUnsaved("waiver", waiverDirty, { label: "Waiver you're writing" });

  // The edit-one-game and bulk-edit dialogs.
  const editGame = editGameDialog.game;
  const editGameDirty =
    editGameDialog.open &&
    Boolean(editGame) &&
    (editRinkId !== (editGame.rink?.id ?? "") ||
      !(editStartsAt && editStartsAt.isSame(dayjs(editGame.startsAt))) ||
      editDurationMinutes !== String(editGame.durationMinutes ?? 60));
  useReportUnsaved("edit-game", editGameDirty, { label: "Changes to a game" });
  const bulkEditDirty =
    bulkEditDialog.open && (bulkEditRinkId !== "__unchanged__" || bulkEditStartsAt !== null || bulkEditDurationMinutes !== "");
  useReportUnsaved("bulk-edit", bulkEditDirty, { label: "Changes to several games" });

  const normalizeVenmoHandle = (raw) => {
    const stripped = raw.trim().replace(/^@+/, "");
    return stripped ? `@${stripped}` : "";
  };

  React.useEffect(() => {
    fetchMySubscription().then(setUserSubscription).catch(() => {});
  }, []);

  React.useEffect(() => {
    // Wait for the subscription to load: before it does, isLaunchTier is true for everyone.
    if (userSubscription && isLaunchTier && payoutMethod === "stripe") {
      setPayoutMethod("venmo");
      // The app made this change, not the organizer, so it isn't an unsaved edit.
      setSettingsBaseline((base) => (base ? { ...base, payoutMethod: "venmo" } : base));
    }
  }, [userSubscription, isLaunchTier, payoutMethod]);

  React.useEffect(() => {
    if (!leagueId) return;

    getMyLeagues()
      .then((myLeagues) => {
        const myLeague = Array.isArray(myLeagues) ? myLeagues.find((l) => l.id === leagueId) : null;
        if (!myLeague || !ORGANIZER_ROLES.has(myLeague.userRole)) {
          setForbidden(true);
          setLoading(false);
          return Promise.resolve(null);
        }
        return Promise.all([
          getLeague(leagueId),
          getRinks(leagueId),
          getLeagueGames(leagueId),
          getNeighboringCities(leagueId),
          getLeagueWaiversAdmin(leagueId).catch(() => []),
          getAllowedMembers(leagueId).catch(() => []),
        ]);
      })
      .then((results) => {
        if (!results) return;
        const [leagueData, rinksList, gamesList, citiesList, waiversList, membersList] = results;
        setNeighboringCities(Array.isArray(citiesList) ? citiesList : []);
        setLeagueGames(Array.isArray(gamesList) ? gamesList : []);
        setWaivers(Array.isArray(waiversList) ? waiversList : []);
        setLeague(leagueData);
        setExistingRinks(Array.isArray(rinksList) ? rinksList : []);
        const form = leagueToForm(leagueData);
        setLeagueName(form.leagueName);
        setLeagueType(form.leagueType);
        setSkillLevel(form.skillLevel);
        setSelectedPlan(form.selectedPlan);
        setPayoutMethod(form.payoutMethod);
        setSavedPayoutMethod(leagueData.payoutMethod ?? "stripe");
        setVenmoHandle(form.venmoHandle);
        setCity(form.city);
        setState(form.state);
        setPlayerCostDollars(form.playerCostDollars);
        setGoalieCostDollars(form.goalieCostDollars);
        setMaxSkaterSpots(form.maxSkaterSpots);
        setMaxGoalieSpots(form.maxGoalieSpots);
        initialCapacityRef.current = (leagueData.maxSkaterSpots ?? 0) + (leagueData.maxGoalieSpots ?? 0);
        setGamePaymentType(form.gamePaymentType);
        setIceTimeCostDollars(form.iceTimeCostDollars);
        setVisibility(form.visibility);
        setUseAllowedList(form.useAllowedList);
        setSettingsBaseline(form);
        setAllowedMembers(Array.isArray(membersList) ? membersList : []);
      })
      .catch((err) => setLoadError(loadErrorMessage(err, "Failed to load league.")))
      .finally(() => setLoading(false));
  }, [leagueId]);

  React.useEffect(() => {
    if ((activeTab !== 2 && activeTab !== 3) || !leagueId) return;
    setPaymentsLoading(true);
    setPaymentsError("");
    getLeaguePayments(leagueId)
      .then(setLeaguePayments)
      .catch((err) => setPaymentsError(loadErrorMessage(err, "Failed to load payments.")))
      .finally(() => setPaymentsLoading(false));
  }, [activeTab, leagueId]);

  const handleTogglePayment = async (paymentId, currentStatus) => {
    const newStatus = currentStatus === "succeeded" ? "pending" : "succeeded";
    setTogglingPayment((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await updatePaymentStatus(paymentId, newStatus);
      setLeaguePayments((prev) =>
        prev.map((p) =>
          p.id === paymentId
            ? { ...p, status: newStatus, gameRegistration: p.gameRegistration ? { ...p.gameRegistration, hasPaid: newStatus === "succeeded" } : p.gameRegistration }
            : p
        )
      );
    } finally {
      setTogglingPayment((prev) => ({ ...prev, [paymentId]: false }));
    }
  };

  const handleMarkAllPaid = async () => {
    const { payments } = confirmMarkAll;
    setConfirmMarkAll({ open: false, name: "", payments: [] });
    for (const p of payments) {
      await handleTogglePayment(p.id, p.status);
    }
  };

  const handleUpdate = async (e) => {
    e?.preventDefault();
    if (!leagueId) return;

    if (existingRinks.length === 0) {
      setSaveState({ loading: false, message: "", error: "At least one rink is required." });
      return;
    }

    const skatersNum = parseInt(maxSkaterSpots, 10);
    const goaliesNum = parseInt(maxGoalieSpots, 10);
    const totalCapacity = (isNaN(skatersNum) ? 0 : skatersNum) + (isNaN(goaliesNum) ? 0 : goaliesNum);
    // Only block capacity increases past the plan limit, so legacy leagues can still save other changes.
    if (totalCapacity > capacityLimit && totalCapacity > initialCapacityRef.current) {
      setSaveState({ loading: false, message: "", error: `Total roster capacity (${totalCapacity}) exceeds your ${activePlanDef.name} plan limit of ${participantLimit}. Reduce capacity or upgrade your plan.` });
      return;
    }

    setSaveState({ loading: true, message: "", error: "" });
    // What is being saved. If the organizer keeps typing while the request runs, that stays unsaved.
    const savedForm = currentSettings;
    try {
      await updateLeague(leagueId, {
        name: leagueName,
        leagueType,
        skillLevel,
        planId: selectedPlan,
        payoutMethod,
        venmoHandle: payoutMethod === "venmo" ? (normalizeVenmoHandle(venmoHandle) || undefined) : undefined,
        city: city.trim() || null,
        state: state.trim() || null,
        playerCost:
          payoutMethod !== "none" && playerCostDollars !== "" ? Math.round(parseFloat(playerCostDollars) * 100) : 0,
        goalieCost:
          payoutMethod !== "none" && goalieCostDollars !== "" ? Math.round(parseFloat(goalieCostDollars) * 100) : 0,
        maxSkaterSpots: maxSkaterSpots !== "" ? parseInt(maxSkaterSpots, 10) : null,
        maxGoalieSpots: maxGoalieSpots !== "" ? parseInt(maxGoalieSpots, 10) : null,
        gamePaymentType: payoutMethod === "none" ? "DROP_IN" : gamePaymentType,
        iceTimeCost: payoutMethod !== "none" && iceTimeCostDollars !== "" ? Math.round(parseFloat(iceTimeCostDollars) * 100) : null,
        visibility,
        useAllowedList: visibility === "PRIVATE" ? useAllowedList : false,
      });

      for (const removal of pendingRinkRemovals) {
        await reassignAndRemoveRink(leagueId, removal.rinkId, removal.replacementRinkId);
      }

      for (const nc of pendingNeighboringCities) {
        await addNeighboringCity(leagueId, { city: nc.city, state: nc.state });
      }

      setSaveState({ loading: false, message: "League updated.", error: "" });
      setSettingsBaseline(savedForm);
      setPendingRinkRemovals([]);
      if (pendingRinkRemovals.length > 0) {
        const gamesList = await getLeagueGames(leagueId);
        setLeagueGames(Array.isArray(gamesList) ? gamesList : []);
      }

      setPendingNeighboringCities([]);
      const updatedCities = await getNeighboringCities(leagueId);
      setNeighboringCities(Array.isArray(updatedCities) ? updatedCities : []);
    } catch (error) {
      setSaveState({
        loading: false,
        message: "",
        error: error instanceof Error ? error.message : "Failed to update league",
      });
    }
  };

  const handleAddRink = async () => {
    const name = rinkName.trim();
    const address = rinkAddress.trim();
    if (!name || !address) {
      setAddRinkError("Rink name and address are required.");
      return;
    }
    setAddRinkError("");
    setAddRinkLoading(true);
    try {
      const created = await createRink(leagueId, { name, address, rinkPhone: rinkPhone.trim() || undefined });
      setExistingRinks((prev) => [...prev, created]);
      setRinkName("");
      setRinkAddress("");
      setRinkPhone("");
    } catch (err) {
      setAddRinkError(err instanceof Error ? err.message : "Failed to add rink.");
    } finally {
      setAddRinkLoading(false);
    }
  };

  const handleAddNeighboringCity = () => {
    const cityVal = neighboringCity.trim();
    const st = neighboringCityState.trim().toUpperCase();
    if (!cityVal || !st) { setAddCityError("City and state are required."); return; }
    const alreadyExists = neighboringCities.some(c => c.city.toLowerCase() === cityVal.toLowerCase() && c.state === st)
      || pendingNeighboringCities.some(c => c.city.toLowerCase() === cityVal.toLowerCase() && c.state === st);
    if (alreadyExists) { setAddCityError("That city/state is already added."); return; }
    setAddCityError("");
    setPendingNeighboringCities(prev => [...prev, { city: cityVal, state: st }]);
    setNeighboringCity("");
    setNeighboringCityState("");
  };

  const handleRemoveNeighboringCity = async (cityId) => {
    try {
      await removeNeighboringCity(leagueId, cityId);
      setNeighboringCities(prev => prev.filter(c => c.id !== cityId));
    } catch (err) {
      setSaveState({ loading: false, message: "", error: err instanceof Error ? err.message : "Failed to remove city." });
    }
  };

  const handleRemoveRink = async (rinkId, rinkNameVal) => {
    setRinkDeleteError("");
    const result = await deleteRink(leagueId, rinkId);
    if (result && result.conflict) {
      setRinkConflictDialog({
        open: true,
        rinkId,
        rinkName: rinkNameVal,
        affectedGames: result.data?.affectedGames ?? [],
        replacementRinkId: "",
      });
      return;
    }
    setExistingRinks(prev => prev.filter(r => r.id !== rinkId));
  };

  const handleConfirmRinkReassign = () => {
    const { rinkId, rinkName: rinkNameVal, replacementRinkId } = rinkConflictDialog;
    if (!replacementRinkId) return;
    setPendingRinkRemovals(prev => [...prev, { rinkId, rinkName: rinkNameVal, replacementRinkId }]);
    setExistingRinks(prev => prev.filter(r => r.id !== rinkId));
    setRinkConflictDialog({ open: false, rinkId: "", rinkName: "", affectedGames: [], replacementRinkId: "" });
  };

  const handleExpandSignatures = async (waiverId) => {
    if (signaturesMap[waiverId]) return; // already loaded
    setSignaturesMap((prev) => ({ ...prev, [waiverId]: { loading: true, rows: [] } }));
    try {
      const rows = await getWaiverSignatures(leagueId, waiverId);
      setSignaturesMap((prev) => ({ ...prev, [waiverId]: { loading: false, rows: Array.isArray(rows) ? rows : [] } }));
    } catch {
      setSignaturesMap((prev) => ({ ...prev, [waiverId]: { loading: false, rows: [] } }));
    }
  };

  const handleOpenWaiverDialog = (waiver = null) => {
    const w = waiver && typeof waiver === "object" && "id" in waiver ? waiver : null;
    setEditingWaiver(w);
    setWaiverName(w?.name ?? "");
    setWaiverHtml(w?.contentHtml ?? "");
    setWaiverError("");
    setWaiverDialogOpen(true);
  };

  const handleLoadTemplate = () => {
    const superAdmin = league?.superAdmin;
    const ownerName = superAdmin
      ? [superAdmin.firstName, superAdmin.lastName].filter(Boolean).join(" ")
      : null;
    const rinkNames = existingRinks.map((r) => r.name).filter(Boolean);
    setWaiverName(STANDARD_HOCKEY_WAIVER_NAME);
    setWaiverHtml(buildStandardHockeyWaiverHtml({
      ownerName,
      rinkNames,
      leagueName: leagueName || league?.name,
    }));
  };

  const waiverHasContent = (html) => {
    if (!html) return false;
    // Strip HTML tags; TipTap's empty state is "<p></p>" which has no actual text
    return html.replace(/<[^>]*>/g, "").trim().length > 0;
  };

  const handleSaveWaiver = async () => {
    if (!waiverName.trim() || !waiverHasContent(waiverHtml)) {
      setWaiverError("Name and content are required.");
      return;
    }
    setWaiverSaving(true);
    setWaiverError("");
    try {
      if (editingWaiver) {
        const updated = await updateWaiverContent(leagueId, editingWaiver.id, {
          name: waiverName.trim(),
          contentHtml: waiverHtml,
        });
        setWaivers((prev) => prev.map((w) => (w.id === editingWaiver.id ? { ...w, ...updated } : w)));
      } else {
        const created = await createWaiver(leagueId, {
          name: waiverName.trim(),
          contentHtml: waiverHtml,
          version: 1,
        });
        setWaivers((prev) => [...prev, created]);
      }
      setWaiverDialogOpen(false);
    } catch (err) {
      setWaiverError(err instanceof Error ? err.message : "Failed to save waiver.");
    } finally {
      setWaiverSaving(false);
    }
  };

  const handleToggleWaiver = async (waiverId, currentActive) => {
    try {
      const updated = await setWaiverActive(leagueId, waiverId, !currentActive);
      setWaivers((prev) => prev.map((w) => (w.id === waiverId ? { ...w, isActive: updated.isActive } : w)));
    } catch (err) {
      setSaveState({ loading: false, message: "", error: err instanceof Error ? err.message : "Failed to update waiver." });
    }
  };

  const handleOpenEditGame = (game) => {
    setEditGameDialog({ open: true, game });
    setEditRinkId(game.rink?.id ?? "");
    setEditStartsAt(dayjs(game.startsAt));
    setEditDurationMinutes(String(game.durationMinutes ?? 60));
    setEditError("");
  };

  const handleSaveEditGame = async () => {
    if (!editStartsAt || !editStartsAt.isValid()) {
      setEditError("Please select a valid date and time.");
      return;
    }
    if (!editStartsAt.isAfter(dayjs())) {
      setEditError("Game time must be in the future.");
      return;
    }
    const duration = parseInt(editDurationMinutes, 10);
    if (!Number.isInteger(duration) || duration < 1) {
      setEditError("Duration must be at least 1 minute.");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await updateGame(editGameDialog.game.id, {
        rinkId: editRinkId || null,
        startsAt: editStartsAt.toISOString(),
        durationMinutes: duration,
      });
      const updatedRink = existingRinks.find((r) => r.id === editRinkId) ?? null;
      setLeagueGames((prev) =>
        prev.map((g) =>
          g.id !== editGameDialog.game.id
            ? g
            : { ...g, startsAt: editStartsAt.toISOString(), durationMinutes: duration, rink: updatedRink, arenaName: updatedRink?.name ?? g.arenaName }
        )
      );
      setEditGameDialog({ open: false, game: null });
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to update game.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelGame = async () => {
    setCancelGameLoading(true);
    setCancelGameError("");
    try {
      await cancelGame(cancelGameDialog.game.id);
      setLeagueGames((prev) =>
        prev.map((g) => g.id === cancelGameDialog.game.id ? { ...g, status: "CANCELLED" } : g)
      );
      setCancelGameDialog({ open: false, game: null });
    } catch (err) {
      setCancelGameError(err instanceof Error ? err.message : "Failed to cancel game.");
    } finally {
      setCancelGameLoading(false);
    }
  };

  const handleDeleteGame = async () => {
    setDeleteGameLoading(true);
    setDeleteGameError("");
    try {
      await deleteGame(deleteGameDialog.game.id);
      setLeagueGames((prev) => prev.filter((g) => g.id !== deleteGameDialog.game.id));
      setDeleteGameDialog({ open: false, game: null });
    } catch (err) {
      setDeleteGameError(err instanceof Error ? err.message : "Failed to delete game.");
    } finally {
      setDeleteGameLoading(false);
    }
  };

  const toggleGameSelection = (gameId) => {
    setSelectedGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const toggleSelectAllOnPage = (pagedGames) => {
    const pageIds = pagedGames.map((g) => g.id);
    const allSelected = pageIds.every((id) => selectedGameIds.includes(id));
    if (allSelected) {
      setSelectedGameIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedGameIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const toBulkEditFailureReason = (err) => {
    const msg = err?.message || "";
    if (msg.includes("future")) return "This game is already in the past and cannot be rescheduled";
    if (msg.includes("admin") || msg.includes("permission")) return "You don't have permission to edit this game";
    if (msg.includes("Rink not found")) return "The selected rink is not available for this game";
    if (msg.includes("Game not found")) return "Game no longer exists";
    return "Could not be updated";
  };

  const handleOpenBulkEdit = () => {
    setActionsMenuAnchor(null);
    setBulkEditRinkId("__unchanged__");
    setBulkEditStartsAt(null);
    setBulkEditDurationMinutes("");
    setBulkEditError("");
    setBulkEditFailures([]);
    setBulkEditDialog({ open: true });
  };

  const handleSaveBulkEdit = async () => {
    if (bulkEditStartsAt !== null) {
      if (!bulkEditStartsAt.isValid()) {
        setBulkEditError("Please select a valid date and time.");
        return;
      }
      if (!bulkEditStartsAt.isAfter(dayjs())) {
        setBulkEditError("Game time must be in the future.");
        return;
      }
    }
    if (bulkEditDurationMinutes !== "") {
      const d = parseInt(bulkEditDurationMinutes, 10);
      if (!Number.isInteger(d) || d < 1) {
        setBulkEditError("Duration must be at least 1 minute.");
        return;
      }
    }
    if (bulkEditStartsAt === null && bulkEditDurationMinutes === "" && bulkEditRinkId === "__unchanged__") {
      setBulkEditError("Please change at least one field.");
      return;
    }
    setBulkEditSaving(true);
    setBulkEditError("");
    setBulkEditFailures([]);
    const selectedGames = leagueGames.filter((g) => selectedGameIds.includes(g.id));
    const failures = [];
    for (const game of selectedGames) {
      try {
        const payload = {
          rinkId: bulkEditRinkId !== "__unchanged__" ? (bulkEditRinkId || null) : (game.rink?.id ?? null),
          startsAt: bulkEditStartsAt !== null ? bulkEditStartsAt.toISOString() : game.startsAt,
          durationMinutes: bulkEditDurationMinutes !== "" ? parseInt(bulkEditDurationMinutes, 10) : (game.durationMinutes ?? 60),
        };
        await updateGame(game.id, payload);
        const updatedRink = bulkEditRinkId !== "__unchanged__"
          ? (existingRinks.find((r) => r.id === bulkEditRinkId) ?? null)
          : game.rink;
        setLeagueGames((prev) =>
          prev.map((g) =>
            g.id !== game.id ? g : {
              ...g,
              startsAt: payload.startsAt,
              durationMinutes: payload.durationMinutes,
              rink: updatedRink,
              arenaName: updatedRink?.name ?? g.arenaName,
            }
          )
        );
      } catch (err) {
        const isInPast = dayjs(game.startsAt).isBefore(dayjs());
        failures.push({
          label: dayjs(game.startsAt).format("MMM D [at] h:mm A") + (isInPast ? " (past)" : ""),
          reason: toBulkEditFailureReason(err),
        });
      }
    }
    setBulkEditSaving(false);
    if (failures.length === 0) {
      setBulkEditDialog({ open: false });
      setSelectedGameIds([]);
    } else {
      setBulkEditFailures(failures);
    }
  };

  const handleOpenBulkDelete = () => {
    setActionsMenuAnchor(null);
    setBulkDeleteError("");
    setBulkDeleteDialog({ open: true });
  };

  const handleBulkDelete = async () => {
    setBulkDeleteLoading(true);
    setBulkDeleteError("");
    const deletableGames = leagueGames.filter(
      (g) => selectedGameIds.includes(g.id) && (g.skaterSpotsFilled ?? 0) === 0 && (g.goalieSpotsFilled ?? 0) === 0
    );
    let successCount = 0;
    let failCount = 0;
    for (const game of deletableGames) {
      try {
        await deleteGame(game.id);
        setLeagueGames((prev) => prev.filter((g) => g.id !== game.id));
        successCount++;
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    if (failCount === 0) {
      setBulkDeleteDialog({ open: false });
      setSelectedGameIds([]);
    } else {
      setBulkDeleteError(`${successCount} game(s) deleted. ${failCount} failed.`);
    }
  };

  const handleAddAllowedMember = async () => {
    const email = allowedMemberEmail.trim().toLowerCase();
    if (!email) return;
    setAllowedMemberLoading(true);
    setAllowedMemberError("");
    try {
      const member = await addAllowedMember(leagueId, email);
      setAllowedMembers((prev) => [...prev, member]);
      setAllowedMemberEmail("");
    } catch (err) {
      setAllowedMemberError(err instanceof Error ? err.message : "Failed to add email.");
    } finally {
      setAllowedMemberLoading(false);
    }
  };

  const bulkParsed = React.useMemo(() => parseEmails(bulkText), [bulkText]);
  const bulkExistingSet = React.useMemo(
    () => new Set(allowedMembers.map((m) => m.email.toLowerCase())),
    [allowedMembers]
  );
  const bulkNewEmails = bulkParsed.emails.filter((e) => !bulkExistingSet.has(e));
  const bulkAlreadyCount = bulkParsed.emails.length - bulkNewEmails.length;

  const appendBulkText = (text) => setBulkText((prev) => (prev.trim() ? `${prev.trim()}\n${text}` : text));

  const loadBulkFile = async (file) => {
    if (!file) return;
    setBulkResult("");
    setAllowedMemberError("");
    if (file.size > 1024 * 1024) {
      setAllowedMemberError("That file is too large (max 1 MB).");
      return;
    }
    try {
      appendBulkText(await file.text());
    } catch {
      setAllowedMemberError("Could not read that file.");
    }
  };

  const handleBulkDrop = (e) => {
    e.preventDefault();
    setBulkDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      loadBulkFile(file);
    } else {
      const text = e.dataTransfer?.getData("text");
      if (text) appendBulkText(text);
    }
  };

  const handleBulkImport = async () => {
    if (bulkNewEmails.length === 0) return;
    setBulkLoading(true);
    setAllowedMemberError("");
    setBulkResult("");
    try {
      const result = await bulkAddAllowedMembers(leagueId, bulkNewEmails);
      if (Array.isArray(result?.members)) setAllowedMembers(result.members);
      const added = result?.added ?? bulkNewEmails.length;
      setBulkText("");
      setBulkResult(`Added ${added} email${added === 1 ? "" : "s"}.`);
    } catch (err) {
      setAllowedMemberError(err instanceof Error ? err.message : "Failed to import emails.");
    } finally {
      setBulkLoading(false);
    }
  };

  const allAllowedSelected = allowedMembers.length > 0 && selectedAllowedIds.length === allowedMembers.length;

  const toggleAllowedSelected = (memberId) => {
    setSelectedAllowedIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleAllAllowedSelected = () => {
    setSelectedAllowedIds(allAllowedSelected ? [] : allowedMembers.map((m) => m.id));
  };

  const handleRemoveSelectedAllowed = async () => {
    if (selectedAllowedIds.length === 0) return;
    setRemovingSelected(true);
    setAllowedMemberError("");
    setBulkResult("");
    try {
      const idsToRemove = selectedAllowedIds;
      await bulkRemoveAllowedMembers(leagueId, idsToRemove);
      setAllowedMembers((prev) => prev.filter((m) => !idsToRemove.includes(m.id)));
      setSelectedAllowedIds([]);
      setBulkResult(`Removed ${idsToRemove.length} email${idsToRemove.length === 1 ? "" : "s"}.`);
    } catch (err) {
      setAllowedMemberError(err instanceof Error ? err.message : "Failed to remove emails.");
    } finally {
      setRemovingSelected(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Container>
    );
  }

  if (forbidden) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You don&apos;t have permission to manage this league.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/my-leagues")}>
          Back to my leagues
        </Button>
      </Container>
    );
  }

  if (loadError || !league) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" onClose={() => navigate("/my-leagues")}>
          {loadError || "League not found."}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/my-leagues")}>
          Back to my leagues
        </Button>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Container maxWidth="md" className="new-league-page">
      <Typography variant="h4" component="h3">
        Manage league
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
        {leagueName}
      </Typography>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant={tabsScroll ? "scrollable" : "fullWidth"}
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3, maxWidth: "100%" }}
      >
        <Tab label="Games" />
        <Tab label="Waivers" />
        <Tab label="Payments" />
        <Tab label="Settings" />
        <Tab label="Notifications" />
      </Tabs>

      <Box component="form" onSubmit={handleUpdate}>
        {activeTab === 4 && <EmailTemplatesEditor leagueId={leagueId} />}

        {activeTab === 3 && (
        <Stack spacing={2}>
          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h5">League Profile</Typography>
                <TextField
                  label="League Name"
                  placeholder="Downtown Adult Pickup Hockey"
                  value={leagueName}
                  onChange={(e) => setLeagueName(e.target.value)}
                  fullWidth
                />
                <FormControl>
                  <FormLabel>League Type</FormLabel>
                  <RadioGroup
                    row
                    value={leagueType}
                    onChange={(e) => setLeagueType(e.target.value)}
                  >
                    <FormControlLabel value="PICKUP" control={<Radio />} label="Pickup" />
                    <FormControlLabel
                      value="COMPETITIVE"
                      control={<Radio />}
                      label="Competitive (league/tournament)"
                    />
                  </RadioGroup>
                </FormControl>
                <TextField
                  select
                  fullWidth
                  label="Skill level"
                  value={skillLevel}
                  onChange={(e) => setSkillLevel(e.target.value)}
                  helperText="Default skill level for all games in this league."
                >
                  <MenuItem value="ALL_WELCOME">All welcome</MenuItem>
                  <MenuItem value="C_BEGINNER">C / Beginner</MenuItem>
                  <MenuItem value="B_C">B/C</MenuItem>
                  <MenuItem value="B">B</MenuItem>
                  <MenuItem value="A_B">A/B</MenuItem>
                  <MenuItem value="A">A</MenuItem>
                </TextField>
                <FormLabel>Primary Location</FormLabel>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <CityStateAutocomplete
                    cityValue={city}
                    stateValue={state}
                    onCityStateChange={(newCity, newState) => {
                      setCity(newCity);
                      setState(newState);
                    }}
                    label="City"
                    placeholder="e.g. Brooklyn Park"
                    sx={{ flex: 1 }}
                  />
                  <StateSelect value={state} onChange={setState} sx={{ width: 180 }} />
                </Stack>

                <Divider />

                <FormLabel>Neighboring Cities</FormLabel>
                <Typography variant="body2" color="text.secondary">
                  Players searching from these cities will also see your games.
                </Typography>
                {neighboringCities.length === 0 && pendingNeighboringCities.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No neighboring cities added yet.</Typography>
                )}
                {neighboringCities.length > 0 && (
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">Saved ({neighboringCities.length})</Typography>
                    {neighboringCities.map((nc) => (
                      <Stack key={nc.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="body2">{nc.city}, {nc.state}</Typography>
                        <IconButton size="small" onClick={() => handleRemoveNeighboringCity(nc.id)} aria-label={`Remove ${nc.city}`}>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
                {pendingNeighboringCities.length > 0 && (
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">Pending ({pendingNeighboringCities.length})</Typography>
                    {pendingNeighboringCities.map((nc, i) => (
                      <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="body2">{nc.city}, {nc.state}</Typography>
                        <IconButton size="small" onClick={() => setPendingNeighboringCities(prev => prev.filter((_, idx) => idx !== i))} aria-label="Remove">
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                  <CityStateAutocomplete
                    cityValue={neighboringCity}
                    stateValue={neighboringCityState}
                    onCityStateChange={(c, s) => { setNeighboringCity(c); setNeighboringCityState(s); }}
                    label="Neighboring city"
                    placeholder="e.g. Maple Grove"
                    sx={{ flex: 1 }}
                  />
                  <StateSelect value={neighboringCityState} onChange={setNeighboringCityState} sx={{ width: 180 }} />
                </Stack>
                {addCityError ? <Alert severity="error" onClose={() => setAddCityError("")}>{addCityError}</Alert> : null}
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleAddNeighboringCity}
                  disabled={!neighboringCity.trim() || !neighboringCityState.trim()}
                >
                  Add neighboring city
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="h5">Rinks</Typography>
                  {isLaunchTier && (
                    <Chip size="small" label="1 rink max · Launch plan" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Rinks are saved immediately when added.
                </Typography>
                {existingRinks.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Existing rinks ({existingRinks.length})
                    </Typography>
                    <Stack spacing={0.5}>
                      {existingRinks.map((r) => {
                        const isOnly = existingRinks.length <= 1;
                        return (
                          <Stack key={r.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                            <Typography variant="body2">
                              {r.name} — {r.address}{r.rinkPhone ? ` · ${r.rinkPhone}` : ""}
                            </Typography>
                            <Tooltip
                              title="At least one rink is required. Add another rink to the list before removing this one."
                              open={isOnly && showOnlyRinkWarning}
                              onClose={() => setShowOnlyRinkWarning(false)}
                              disableHoverListener
                              disableFocusListener
                              disableTouchListener
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (isOnly) { setShowOnlyRinkWarning(true); setTimeout(() => setShowOnlyRinkWarning(false), 4000); return; }
                                  handleRemoveRink(r.id, r.name);
                                }}
                                aria-label={`Remove ${r.name}`}
                              >
                                <DeleteOutlineIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
                {existingRinks.length >= rinkLimit ? (
                  <Alert severity="info">
                    Your <strong>Launch</strong> plan supports {rinkLimit} rink per league.{" "}
                    <strong>Upgrade your plan</strong> to add multiple rinks.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Add a rink</Typography>
                    <TextField
                      label="Rink name"
                      placeholder="e.g. Maple Arena - Rink B"
                      value={rinkName}
                      onChange={(e) => setRinkName(e.target.value)}
                      fullWidth
                    />
                    <AddressAutocomplete
                      value={rinkAddress}
                      onAddressChange={setRinkAddress}
                      label="Address"
                      placeholder="Start typing address or facility name for suggestions"
                    />
                    <PhoneTextField
                      label="Phone (optional)"
                      value={rinkPhone}
                      onChange={setRinkPhone}
                      fullWidth
                    />
                    {addRinkError ? (
                      <Alert severity="error" onClose={() => setAddRinkError("")}>
                        {addRinkError}
                      </Alert>
                    ) : null}
                  </>
                )}
                {rinkDeleteError ? <Alert severity="error" onClose={() => setRinkDeleteError("")}>{rinkDeleteError}</Alert> : null}
                {existingRinks.length < rinkLimit && (
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleAddRink}
                    disabled={addRinkLoading || !rinkName.trim() || !rinkAddress.trim()}
                  >
                    {addRinkLoading ? "Adding…" : "Add rink"}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" className="section-card" id="payment-method-card">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h5">Payment Method</Typography>
                <FormControl>
                  <FormLabel>How should participant payments return to the organizer?</FormLabel>
                  <RadioGroup
                    value={payoutMethod}
                    onChange={(e) => {
                      setPayoutMethod(e.target.value);
                      if (e.target.value === "none") {
                        setVisibility("PRIVATE");
                        setGamePaymentType("DROP_IN");
                      }
                    }}
                  >
                    <FormControlLabel
                      value="stripe"
                      control={<Radio disabled={isLaunchTier} />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <span>Stripe Connect (recommended)</span>
                          {isLaunchTier && (
                            <Typography variant="caption" color="text.secondary">
                              — Not available on the Launch plan. Upgrade to enable.
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                    <FormControlLabel value="venmo" control={<Radio />} label="Venmo" />
                    <FormControlLabel
                      value="none"
                      control={<Radio disabled={organizerCoveredBlocked} />}
                      label="No payment — organizer covers the cost"
                    />
                  </RadioGroup>
                </FormControl>
                {organizerCoveredBlocked && (
                  <Alert severity="warning" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
                    This league already has payments recorded, so it can&apos;t be switched to organizer-covered. It&apos;s best to create a new league instead.
                  </Alert>
                )}
                {payoutMethod === "venmo" ? (
                  <TextField
                    label="Organizer Venmo Handle"
                    placeholder="organizer's venmo handler"
                    value={venmoHandle}
                    onChange={(e) => setVenmoHandle(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }}
                  />
                ) : null}
                {payoutMethod !== "none" && (
                  <Typography variant="body2" color="text.secondary">
                    Subscription billing is always charged to the organizer.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h5">League capacity & cost</Typography>
                <FormLabel>Roster Size</FormLabel>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Skaters per game"
                    type="number"
                    inputProps={{ min: 0, max: Number.isFinite(capacityLimit) ? capacityLimit : undefined }}
                    placeholder="20"
                    helperText="Max skaters per game"
                    value={maxSkaterSpots}
                    onChange={(e) => {
                      setMaxSkaterSpots(e.target.value);
                      recalcPlayerCost(iceTimeCostDollars, e.target.value, plannedGameCount, gamePaymentType);
                    }}
                    error={
                      maxSkaterSpots !== "" && maxGoalieSpots !== "" &&
                      parseInt(maxSkaterSpots, 10) + parseInt(maxGoalieSpots, 10) > capacityLimit
                    }
                  />
                  <TextField
                    label="Goalies per game"
                    type="number"
                    inputProps={{ min: 0, max: Number.isFinite(capacityLimit) ? capacityLimit : undefined }}
                    placeholder="2"
                    helperText="Max goalies per game"
                    value={maxGoalieSpots}
                    onChange={(e) => setMaxGoalieSpots(e.target.value)}
                    error={
                      maxSkaterSpots !== "" && maxGoalieSpots !== "" &&
                      parseInt(maxSkaterSpots, 10) + parseInt(maxGoalieSpots, 10) > capacityLimit
                    }
                  />
                </Stack>
                {(() => {
                  const skaters = parseInt(maxSkaterSpots, 10);
                  const goalies = parseInt(maxGoalieSpots, 10);
                  const total = (isNaN(skaters) ? 0 : skaters) + (isNaN(goalies) ? 0 : goalies);
                  const showCount = userSubscription && (maxSkaterSpots !== "" || maxGoalieSpots !== "");
                  if (!showCount) return null;
                  const overLimit = total > participantLimit;
                  return (
                    <Typography variant="caption" color={overLimit ? "error" : "text.secondary"}>
                      {overLimit
                        ? `Total exceeds your ${activePlanDef.name} plan limit of ${participantLimit} participants. Reduce capacity or upgrade your plan.`
                        : `Total capacity: ${total} / ${participantLimit} participants (${activePlanDef.name} plan)`}
                    </Typography>
                  );
                })()}
                {(() => {
                  const locked = pricingLocked;
                  return (
                    <>
                      {payoutMethod === "none" ? (
                        <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
                          Players won&apos;t be charged, and each player can sign up for all games or just the individual games they plan to attend (drop-in). Payment type and pricing are hidden because the organizer is covering the cost.
                        </Alert>
                      ) : (
                        <>
                      <FormControl>
                        <FormLabel>Payment Type</FormLabel>
                        <RadioGroup
                          row
                          value={gamePaymentType}
                          onChange={(e) => {
                            setGamePaymentType(e.target.value);
                            recalcPlayerCost(iceTimeCostDollars, maxSkaterSpots, plannedGameCount, e.target.value);
                          }}
                          sx={{ opacity: locked ? 0.5 : 1, pointerEvents: locked ? "none" : "auto" }}
                        >
                          <FormControlLabel value="DROP_IN" control={<Radio />} label="Drop-in (players pay per game)" />
                          <FormControlLabel value="FULL_LEAGUE" control={<Radio />} label="Full league (players pay once for all games)" />
                        </RadioGroup>
                      </FormControl>
                      <TextField
                        label="Total ice rental cost for this league ($)"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="e.g. 300"
                        value={iceTimeCostDollars}
                        onChange={(e) => {
                          setIceTimeCostDollars(e.target.value);
                          recalcPlayerCost(e.target.value, maxSkaterSpots, plannedGameCount, gamePaymentType);
                        }}
                        helperText={(() => {
                          const cost = parseFloat(iceTimeCostDollars);
                          const spots = parseInt(maxSkaterSpots, 10);
                          const hasValidInputs = iceTimeCostDollars !== "" && !isNaN(cost) && maxSkaterSpots !== "" && !isNaN(spots) && spots > 0;
                          if (gamePaymentType === "FULL_LEAGUE") {
                            if (hasValidInputs) {
                              const breakEven = (cost / spots).toFixed(2);
                              const current = playerCostDollars !== "" ? parseFloat(playerCostDollars).toFixed(2) : null;
                              return `At ${spots} skaters, break-even is $${breakEven}/player.${current != null ? ` Your current player price: $${current}.` : ""}`;
                            }
                            return "Enter your total ice rental cost per session to see break-even pricing suggestions.";
                          }
                          // DROP_IN
                          if (hasValidInputs) {
                            const totalBreakEven = (cost / spots).toFixed(2);
                            const games = parseInt(plannedGameCount, 10);
                            if (!isNaN(games) && games > 0) {
                              return `At ${spots} skaters across ${games} games, break-even is $${(cost / spots / games).toFixed(2)}/player per game.${locked ? "" : " Player cost updated below."}`;
                            }
                            return `At ${spots} skaters, total break-even is $${totalBreakEven}/player. Divide that by the number of games you plan to host.`;
                          }
                          return "Enter your total ice rental cost per session to see break-even pricing suggestions.";
                        })()}
                        fullWidth
                      />
                      {gamePaymentType === "DROP_IN" && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: -0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            ÷ estimated games:
                          </Typography>
                          <TextField
                            type="number"
                            size="small"
                            variant="standard"
                            inputProps={{ min: 1, step: 1 }}
                            placeholder="e.g. 10"
                            value={plannedGameCount}
                            onChange={(e) => {
                              setPlannedGameCount(e.target.value);
                              recalcPlayerCost(iceTimeCostDollars, maxSkaterSpots, e.target.value, gamePaymentType);
                            }}
                            sx={{ width: 90 }}
                          />
                        </Stack>
                      )}
                      <FormLabel>Cost per game - Goalie cost defaults to $0</FormLabel>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                        <TextField
                          label="Player cost ($)"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          placeholder="7"
                          value={playerCostDollars}
                          onChange={(e) => setPlayerCostDollars(e.target.value)}
                          disabled={locked}
                        />
                        <TextField
                          label="Goalie cost ($)"
                          type="number"
                          inputProps={{ min: 0, step: 0.01 }}
                          placeholder="0"
                          value={goalieCostDollars}
                          onChange={(e) => setGoalieCostDollars(e.target.value)}
                          helperText="Default $0"
                          disabled={locked}
                        />
                      </Stack>
                        </>
                      )}
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {payoutMethod !== "none" && (
                        <Alert severity={locked ? "warning" : priceOverrideEnabled ? "success" : "info"} sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" }, flex: 1 }}>
                          {locked
                            ? "Pricing is locked because participants have already signed up. Unlock to override."
                            : priceOverrideEnabled
                            ? "Pricing override is active. Existing registrations used the previous price."
                            : "Pricing cannot be changed after participants have joined the league."}
                        </Alert>
                        )}
                        {locked && payoutMethod !== "none" && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<LockOpenIcon />}
                            onClick={() => { setPriceOverrideAcknowledged(false); setPriceOverrideDialogOpen(true); }}
                          >
                            Unlock pricing
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<HistoryIcon />}
                          onClick={() => {
                            setPriceHistoryLoading(true);
                            setPriceHistoryDialogOpen(true);
                            getLeaguePriceHistory(leagueId)
                              .then(setPriceHistory)
                              .catch(() => setPriceHistory([]))
                              .finally(() => setPriceHistoryLoading(false));
                          }}
                        >
                          Price history
                        </Button>
                      </Stack>
                    </>
                  );
                })()}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h5">League Visibility</Typography>
                <FormControl>
                  <FormLabel>Who can find and join this league?</FormLabel>
                  <RadioGroup value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                    <FormControlLabel value="PUBLIC" control={<Radio disabled={payoutMethod === "none"} />} label="Public — visible in search results, open sign-ups" />
                    <FormControlLabel value="PRIVATE" control={<Radio />} label="Private — hidden from search" />
                  </RadioGroup>
                </FormControl>
                {payoutMethod === "none" && (
                  <Alert
                    severity="info"
                    icon={<LockOutlinedIcon fontSize="inherit" />}
                    sx={{ "& .MuiAlert-message": { width: "100%" } }}
                  >
                    <Stack spacing={1.5} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                      <Typography variant="body2">
                        Organizer-covered leagues are always private. This protects the league from uninvited players showing up without an explicit invite.
                      </Typography>
                      <Button
                        type="button"
                        size="small"
                        color="info"
                        variant="contained"
                        disableElevation
                        sx={{ textTransform: "none" }}
                        onClick={() => {
                          document.getElementById("payment-method-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                      >
                        Switch to a different payment method
                      </Button>
                    </Stack>
                  </Alert>
                )}
                {visibility === "PRIVATE" && (
                  <Stack spacing={2} sx={{ pl: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useAllowedList}
                          onChange={(e) => setUseAllowedList(e.target.checked)}
                        />
                      }
                      label="Restrict sign-ups to an allowed list"
                    />
                    {useAllowedList ? (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Only emails on the allowed list below can view the league and register for games. Add them below — changes save immediately.
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        Anyone with the direct link (your QR code URL) can view and sign up. The league won&apos;t appear in public search.
                      </Alert>
                    )}
                    {useAllowedList && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2" color="text.secondary">Allowed emails</Typography>
                        {allowedMembers.length > 0 && (
                          <Stack spacing={0.5}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={allAllowedSelected}
                                    indeterminate={selectedAllowedIds.length > 0 && !allAllowedSelected}
                                    onChange={toggleAllAllowedSelected}
                                  />
                                }
                                label={
                                  <Typography variant="body2" color="text.secondary">
                                    {selectedAllowedIds.length > 0
                                      ? `${selectedAllowedIds.length} of ${allowedMembers.length} selected`
                                      : `Select all (${allowedMembers.length})`}
                                  </Typography>
                                }
                              />
                              {selectedAllowedIds.length > 0 && (
                                <Button
                                  type="button"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteOutlineIcon fontSize="small" />}
                                  onClick={handleRemoveSelectedAllowed}
                                  disabled={removingSelected}
                                >
                                  {removingSelected ? "Removing…" : `Remove selected (${selectedAllowedIds.length})`}
                                </Button>
                              )}
                            </Stack>
                            <Stack spacing={0.5} sx={{ maxHeight: 360, overflowY: "auto" }}>
                              {allowedMembers.map((m) => (
                                <FormControlLabel
                                  key={m.id}
                                  sx={{ m: 0, pr: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedAllowedIds.includes(m.id)}
                                      onChange={() => toggleAllowedSelected(m.id)}
                                    />
                                  }
                                  label={<Typography variant="body2">{m.email}</Typography>}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <TextField
                            label="Email address"
                            type="email"
                            size="small"
                            value={allowedMemberEmail}
                            onChange={(e) => setAllowedMemberEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddAllowedMember(); } }}
                            sx={{ flexGrow: 1 }}
                          />
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleAddAllowedMember}
                            disabled={allowedMemberLoading || !allowedMemberEmail.trim()}
                          >
                            {allowedMemberLoading ? "Adding…" : "Add"}
                          </Button>
                        </Stack>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
                          Add many at once
                        </Typography>
                        <Box
                          onDragOver={(e) => { e.preventDefault(); setBulkDragging(true); }}
                          onDragLeave={() => setBulkDragging(false)}
                          onDrop={handleBulkDrop}
                          sx={{
                            border: "2px dashed",
                            borderColor: bulkDragging ? "primary.main" : "divider",
                            bgcolor: bulkDragging ? "action.hover" : "transparent",
                            borderRadius: 2,
                            p: 2,
                            transition: "all 0.15s",
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <UploadFileIcon color="action" />
                              <Typography variant="body2" color="text.secondary">
                                Drag &amp; drop a CSV file here, or paste a list of emails below.
                              </Typography>
                              <Button size="small" type="button" onClick={() => bulkFileInputRef.current?.click()}>
                                Browse
                              </Button>
                              <input
                                ref={bulkFileInputRef}
                                type="file"
                                accept=".csv,.txt,text/csv,text/plain"
                                hidden
                                onChange={(e) => { loadBulkFile(e.target.files?.[0]); e.target.value = ""; }}
                              />
                            </Stack>
                            <TextField
                              multiline
                              minRows={3}
                              maxRows={10}
                              size="small"
                              placeholder={"alice@example.com, bob@example.com\ncarol@example.com"}
                              value={bulkText}
                              onChange={(e) => { setBulkText(e.target.value); setBulkResult(""); }}
                            />
                            {bulkText.trim() && (
                              <Typography variant="body2" color="text.secondary">
                                {`Found ${bulkParsed.emails.length} email${bulkParsed.emails.length === 1 ? "" : "s"}: ${bulkNewEmails.length} new`}
                                {bulkAlreadyCount > 0 && `, ${bulkAlreadyCount} already on the list`}
                                {bulkParsed.invalid.length > 0 &&
                                  `, ${bulkParsed.invalid.length} invalid skipped (${bulkParsed.invalid.slice(0, 3).join(", ")}${bulkParsed.invalid.length > 3 ? ", ..." : ""})`}
                                .
                              </Typography>
                            )}
                            <Stack direction="row" spacing={1}>
                              <Button
                                type="button"
                                variant="outlined"
                                onClick={handleBulkImport}
                                disabled={bulkLoading || bulkNewEmails.length === 0}
                              >
                                {bulkLoading
                                  ? "Importing…"
                                  : bulkNewEmails.length > 0
                                    ? `Add ${bulkNewEmails.length} email${bulkNewEmails.length === 1 ? "" : "s"}`
                                    : "Add emails"}
                              </Button>
                              {bulkText && (
                                <Button type="button" onClick={() => { setBulkText(""); setBulkResult(""); }}>Clear</Button>
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                        {bulkResult && (
                          <Alert severity="success" onClose={() => setBulkResult("")}>{bulkResult}</Alert>
                        )}
                        {allowedMemberError && (
                          <Alert severity="error" onClose={() => setAllowedMemberError("")}>{allowedMemberError}</Alert>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Box className="cta-row">
            <Button type="submit" variant="contained" size="large" disabled={saveState.loading}>
              {saveState.loading ? "Updating…" : "Update"}
            </Button>
            <Button type="button" variant="outlined" onClick={() => navigate("/my-leagues")}>
              Back to my leagues
            </Button>
          </Box>
          {saveState.message ? <Alert severity="success">{saveState.message}</Alert> : null}
          {saveState.error ? <Alert severity="error">{saveState.error}</Alert> : null}
        </Stack>
        )}

        {activeTab === 0 && (
          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Games</Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      navigate("/create-game", { state: { leagueId } })
                    }
                  >
                    + Create Game
                  </Button>
                </Stack>
                {leagueGames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No games yet. Create a game to get skaters on the ice.
                  </Typography>
                ) : (() => {
                  const gamePageSize = gamePageSizeSetting === "all" ? leagueGames.length : Number(gamePageSizeSetting);
                  const totalPages = Math.max(1, Math.ceil(leagueGames.length / gamePageSize));
                  // If games were deleted and the remembered page no longer exists, show the last one.
                  const currentPage = Math.min(gamePage, totalPages);
                  const pagedGames = leagueGames.slice((currentPage - 1) * gamePageSize, currentPage * gamePageSize);
                  return (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 0.5 }}>
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5 }}
                          checked={pagedGames.length > 0 && pagedGames.every((g) => selectedGameIds.includes(g.id))}
                          indeterminate={pagedGames.some((g) => selectedGameIds.includes(g.id)) && !pagedGames.every((g) => selectedGameIds.includes(g.id))}
                          onChange={() => toggleSelectAllOnPage(pagedGames)}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                          {selectedGameIds.length > 0 ? `${selectedGameIds.length} selected` : "Select all"}
                        </Typography>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          disabled={selectedGameIds.length === 0}
                          onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                        >
                          Actions
                        </Button>
                        <Menu
                          anchorEl={actionsMenuAnchor}
                          open={Boolean(actionsMenuAnchor)}
                          onClose={() => setActionsMenuAnchor(null)}
                        >
                          <MenuItem onClick={handleOpenBulkEdit}>Bulk Edit</MenuItem>
                          <MenuItem onClick={handleOpenBulkDelete}>Bulk Delete</MenuItem>
                        </Menu>
                      </Stack>
                      <Stack spacing={1} divider={<Divider flexItem />}>
                        {pagedGames.map((g) => {
                          const d = new Date(g.startsAt);
                          const dateStr = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                          const timeStr = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
                          const isPast = d < new Date();
                          return (
                            <Stack
                              key={g.id}
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                                <Checkbox
                                  size="small"
                                  checked={selectedGameIds.includes(g.id)}
                                  onChange={() => toggleGameSelection(g.id)}
                                  sx={{ mt: -0.5, p: 0.5 }}
                                />
                                <Box>
                                  <Typography variant="body2">
                                    {g.arenaName || g.rink?.name || "Game"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {dateStr} · {timeStr}
                                    {g.rink?.address ? ` · ${g.rink.address}` : ""}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                                    <Chip label={`Skaters: ${g.skaterSpotsFilled ?? 0}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.7rem" }} />
                                    <Chip label={`Goalies: ${g.goalieSpotsFilled ?? 0}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.7rem" }} />
                                    {isPast && <Chip label="Past" size="small" variant="outlined" color="default" sx={{ height: 18, fontSize: "0.7rem" }} />}
                                    {g.status === "CANCELLED" && <Chip label="Cancelled" size="small" color="error" sx={{ height: 18, fontSize: "0.7rem" }} />}
                                  </Stack>
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {g.status !== "CANCELLED" && (
                                  <Tooltip title="Edit game">
                                    <IconButton size="small" type="button" onClick={() => handleOpenEditGame(g)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Button type="button" size="small" variant="text" onClick={() => {
                                  // Remember where we were so Back returns to the same spot, not the top.
                                  sessionStorage.setItem(gamesScrollKey(leagueId), String(window.scrollY));
                                  navigate(`/games/${g.id}/roster`);
                                }}>
                                  View Roster
                                </Button>
                                {g.status !== "CANCELLED" && (
                                  (g.skaterSpotsFilled > 0 || g.goalieSpotsFilled > 0) ? (
                                    <Button type="button" size="small" variant="text" color="warning" onClick={() => setCancelGameDialog({ open: true, game: g })}>
                                      Cancel
                                    </Button>
                                  ) : (
                                    <Button type="button" size="small" variant="text" color="error" onClick={() => setDeleteGameDialog({ open: true, game: g })}>
                                      Delete
                                    </Button>
                                  )
                                )}
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ pt: 1 }}>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, p) => setGamesView(p, gamePageSizeSetting)}
                          size="small"
                          siblingCount={1}
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="text.secondary">Per page:</Typography>
                          <Select
                            size="small"
                            value={gamePageSizeSetting}
                            onChange={(e) => setGamesView(1, e.target.value)}
                            sx={{ fontSize: "0.8rem", height: 28 }}
                          >
                            <MenuItem value="5">5</MenuItem>
                            <MenuItem value="10">10</MenuItem>
                            <MenuItem value="25">25</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                          </Select>
                        </Stack>
                      </Stack>
                    </>
                  );
                })()}
              </Stack>
            </CardContent>
          </Card>
        )}

        {activeTab === 1 && (
          <Card variant="outlined" className="section-card">
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">Waivers</Typography>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenWaiverDialog}
                  >
                    Create Waiver
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Active waivers must be signed by participants before they can join the league.
                </Typography>
                {waivers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No waivers yet. Create one to require participants to sign before joining.
                  </Typography>
                ) : (
                  <Stack spacing={1} divider={<Divider />}>
                    {waivers.map((w) => {
                      const sigState = signaturesMap[w.id];
                      return (
                        <Accordion
                          key={w.id}
                          variant="outlined"
                          disableGutters
                          onChange={(_, expanded) => { if (expanded) handleExpandSignatures(w.id); }}
                          sx={{ "&:before": { display: "none" } }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{w.name}</Typography>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">v{w.version}</Typography>
                                <Chip
                                  label={w.isActive ? "Active" : "Inactive"}
                                  size="small"
                                  color={w.isActive ? "success" : "default"}
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: "0.7rem" }}
                                />
                              </Stack>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Stack direction="row" spacing={1} sx={{ pt: 1, pb: 1.5 }}>
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenWaiverDialog(w)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color={w.isActive ? "warning" : "success"}
                                onClick={() => handleToggleWaiver(w.id, w.isActive)}
                              >
                                {w.isActive ? "Deactivate" : "Activate"}
                              </Button>
                            </Stack>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                              Signed waivers
                            </Typography>
                            {sigState?.loading ? (
                              <Typography variant="body2" color="text.secondary">Loading…</Typography>
                            ) : sigState?.rows?.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">No signatures yet.</Typography>
                            ) : (
                              <Stack spacing={0.5} divider={<Divider sx={{ borderColor: "rgba(0,0,0,0.3)" }} />}>
                                {sigState?.rows?.map((sig) => (
                                  <Stack
                                    key={sig.id}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={0.5}
                                    sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: "action.hover" }}
                                  >
                                    <Box>
                                      <Typography variant="body2">{sig.signerName}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {sig.email ? `${sig.email} · ` : ""}
                                        {new Date(sig.signedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                                      </Typography>
                                    </Box>
                                    {sig.pdfUrl ? (
                                      <Link
                                        href={sig.pdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                        variant="caption"
                                        sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
                                      >
                                        PDF <OpenInNewIcon sx={{ fontSize: 12 }} />
                                      </Link>
                                    ) : (
                                      <Typography variant="caption" color="text.disabled">No PDF</Typography>
                                    )}
                                  </Stack>
                                ))}
                              </Stack>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography variant="h5">Payment Tracking</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setPaymentsLoading(true);
                  setPaymentsError("");
                  getLeaguePayments(leagueId)
                    .then(setLeaguePayments)
                    .catch((err) => setPaymentsError(loadErrorMessage(err, "Failed to load payments.")))
                    .finally(() => setPaymentsLoading(false));
                }}
                disabled={paymentsLoading}
              >
                Refresh
              </Button>
              {[{ value: "all", label: "All" }, { value: "pending", label: "Unpaid" }, { value: "succeeded", label: "Paid" }].map(({ value, label }) => {
                const count = value === "all" ? leaguePayments.length : leaguePayments.filter((p) => p.status === value).length;
                return (
                  <Chip
                    key={value}
                    label={`${label} (${count})`}
                    size="small"
                    onClick={() => setPaymentsFilter(value)}
                    color={paymentsFilter === value ? "primary" : "default"}
                    variant={paymentsFilter === value ? "filled" : "outlined"}
                  />
                );
              })}
            </Stack>
          </Stack>
          {paymentsLoading && <LinearProgress />}
          {paymentsError && <Alert severity="error">{paymentsError}</Alert>}
          {!paymentsLoading && !paymentsError && leaguePayments.length === 0 && (
            <Typography variant="body2" color="text.secondary">No payments recorded yet.</Typography>
          )}
          {!paymentsLoading && leaguePayments.length > 0 && (() => {
            const filtered = paymentsFilter === "all" ? leaguePayments : leaguePayments.filter((p) => p.status === paymentsFilter);
            if (filtered.length === 0) return <Typography variant="body2" color="text.secondary">No {paymentsFilter === "pending" ? "unpaid" : "paid"} payments.</Typography>;

            const byUser = {};
            for (const p of filtered) {
              const user = p.gameRegistration?.user;
              const key = user?.email ?? user?.id ?? "unknown";
              if (!byUser[key]) byUser[key] = { user, payments: [] };
              byUser[key].payments.push(p);
            }

            return <Stack spacing={1} divider={<Divider />}>{Object.values(byUser).map(({ user, payments }) => {
              const userKey = user?.email ?? user?.id ?? "unknown";
              const userName = user ? `${user.firstName} ${user.lastName}` : "Unknown player";
              const pendingPayments = payments.filter((p) => p.status !== "succeeded");
              const totalCents = payments.reduce((sum, p) => sum + (p.amountCents ?? 0), 0);
              const totalLabel = totalCents > 0 ? `$${(totalCents / 100).toFixed(2)}` : null;

              return (
                <Accordion
                  key={userKey}
                  expanded={expandedPaymentUser === userKey}
                  onChange={(_, isExpanded) => setExpandedPaymentUser(isExpanded ? userKey : null)}
                  variant="outlined"
                  disableGutters
                  sx={{ "&:before": { display: "none" }, borderRadius: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                      gap={1}
                      sx={{ flex: 1, mr: 1 }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{userName}</Typography>
                        {user?.email && (
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                        )}
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={gamePaymentType === "FULL_LEAGUE"
                            ? "Entire League"
                            : `${payments.length} game${payments.length !== 1 ? "s" : ""}${pendingPayments.length > 0 ? ` · ${pendingPayments.length} unpaid` : " · all paid"}`}
                          size="small"
                          color={pendingPayments.length > 0 ? "warning" : "success"}
                          variant="outlined"
                        />
                        {totalLabel && (
                          <Typography variant="body2" color="text.secondary">{totalLabel}</Typography>
                        )}
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack spacing={1}>
                      {payments.length > 1 && pendingPayments.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => setConfirmMarkAll({ open: true, name: userName, payments: pendingPayments })}
                          >
                            Mark all paid
                          </Button>
                        </Box>
                      )}
                      <Stack spacing={1} divider={<Divider sx={{ borderColor: "rgba(0,0,0,0.3)" }} />}>
                      {payments.map((p) => {
                        const game = p.gameRegistration?.game;
                        const gameLabel = game
                          ? `${new Date(game.startsAt).toLocaleDateString()} — ${game.arenaName || game.rink?.name || "Game"}${gamePaymentType === "FULL_LEAGUE" ? " · Entire League" : ""}`
                          : "Unknown game";
                        const amountLabel = p.amountCents != null ? `$${(p.amountCents / 100).toFixed(2)}` : "—";
                        const registeredAt = p.gameRegistration?.createdAt
                          ? new Date(p.gameRegistration.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
                          : null;
                        return (
                          <Stack key={p.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2">{gameLabel}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {amountLabel}{p.paymentNote ? ` · ${p.paymentNote}` : ""}
                                {registeredAt ? ` · Registered ${registeredAt}` : ""}
                              </Typography>
                            </Box>
                            <Chip
                              label={p.status === "succeeded" ? "Paid" : p.status === "failed" ? "Failed" : "Pending"}
                              size="small"
                              color={p.status === "succeeded" ? "success" : p.status === "failed" ? "error" : "warning"}
                              variant="outlined"
                            />
                            <Button
                              size="small"
                              variant="text"
                              color={p.status === "succeeded" ? "warning" : "success"}
                              disabled={!!togglingPayment[p.id]}
                              onClick={() => p.status === "succeeded"
                                ? setConfirmUnpaid({ open: true, paymentId: p.id })
                                : handleTogglePayment(p.id, p.status)}
                              sx={{ flexShrink: 0, minWidth: 88 }}
                            >
                              {togglingPayment[p.id] ? "…" : p.status === "succeeded" ? "Mark unpaid" : "Mark paid"}
                            </Button>
                          </Stack>
                        );
                      })}
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}</Stack>;
          })()}
        </Stack>
        )}
      </Box>

      <Dialog open={confirmUnpaid.open} onClose={() => setConfirmUnpaid({ open: false, paymentId: null })}>
        <DialogTitle>Mark as unpaid?</DialogTitle>
        <DialogContent>
          <DialogContentText>This will remove the paid status for this participant. Are you sure?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnpaid({ open: false, paymentId: null })}>Cancel</Button>
          <Button
            color="warning"
            onClick={() => {
              handleTogglePayment(confirmUnpaid.paymentId, "succeeded");
              setConfirmUnpaid({ open: false, paymentId: null });
            }}
          >
            Mark unpaid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmMarkAll.open} onClose={() => setConfirmMarkAll({ open: false, name: "", payments: [] })}>
        <DialogTitle>Mark all games as paid?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark all {confirmMarkAll.payments.length} pending game{confirmMarkAll.payments.length !== 1 ? "s" : ""} as paid for <strong>{confirmMarkAll.name}</strong>. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMarkAll({ open: false, name: "", payments: [] })}>Cancel</Button>
          <Button color="success" variant="contained" onClick={handleMarkAllPaid}>
            Mark all paid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={waiverDialogOpen} maxWidth="md" fullWidth onClose={() => !waiverSaving && setWaiverDialogOpen(false)}>
        <DialogTitle>
          {editingWaiver ? `Edit Waiver — v${(editingWaiver.version ?? 0) + 1} on save` : "Create Waiver"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Waiver name"
              placeholder="e.g. Hockey Participation Waiver"
              value={waiverName}
              onChange={(e) => setWaiverName(e.target.value)}
              fullWidth
              required
            />
            <Button
              type="button"
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleLoadTemplate}
              sx={{ alignSelf: "flex-start" }}
            >
              {editingWaiver ? "Reset to standard hockey waiver template" : "Load standard hockey waiver template"}
            </Button>
            <Typography variant="subtitle2" color="text.secondary">Waiver content</Typography>
            <RichTextEditor value={waiverHtml} onChange={setWaiverHtml} minHeight={300} />
            {waiverError && (
              <Alert severity="error" onClose={() => setWaiverError("")}>{waiverError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaiverDialogOpen(false)} disabled={waiverSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveWaiver}
            disabled={waiverSaving || !waiverName.trim() || !waiverHasContent(waiverHtml)}
          >
            {waiverSaving ? "Saving…" : editingWaiver ? "Save Changes" : "Create Waiver"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editGameDialog.open} maxWidth="xs" fullWidth onClose={() => !editSaving && setEditGameDialog({ open: false, game: null })}>
        <DialogTitle>Edit game</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <DateTimePicker
              label="Date & time"
              value={editStartsAt}
              onChange={(v) => setEditStartsAt(v)}
              minDateTime={dayjs()}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <FormControl fullWidth>
              <InputLabel id="edit-game-rink-label">Rink</InputLabel>
              <Select
                labelId="edit-game-rink-label"
                label="Rink"
                value={editRinkId}
                onChange={(e) => setEditRinkId(e.target.value)}
              >
                <MenuItem value=""><em>No rink assigned</em></MenuItem>
                {existingRinks.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}{r.address ? ` — ${r.address}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Duration (minutes)"
              type="number"
              inputProps={{ min: 1 }}
              value={editDurationMinutes}
              onChange={(e) => setEditDurationMinutes(e.target.value)}
              fullWidth
            />
            {editError && <Alert severity="error" onClose={() => setEditError("")}>{editError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGameDialog({ open: false, game: null })} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditGame} disabled={editSaving}>
            {editSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelGameDialog.open} maxWidth="xs" fullWidth onClose={() => !cancelGameLoading && setCancelGameDialog({ open: false, game: null })}>
        <DialogTitle>Cancel game?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This game has {(cancelGameDialog.game?.skaterSpotsFilled ?? 0) + (cancelGameDialog.game?.goalieSpotsFilled ?? 0)} registered player(s). Cancelling will mark the game as cancelled but preserve all registration records.
          </DialogContentText>
          {cancelGameError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setCancelGameError("")}>{cancelGameError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelGameDialog({ open: false, game: null })} disabled={cancelGameLoading}>Back</Button>
          <Button variant="contained" color="warning" onClick={handleCancelGame} disabled={cancelGameLoading}>
            {cancelGameLoading ? "Cancelling…" : "Cancel Game"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteGameDialog.open} maxWidth="xs" fullWidth onClose={() => !deleteGameLoading && setDeleteGameDialog({ open: false, game: null })}>
        <DialogTitle>Delete game?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This game has no registrations and will be permanently deleted. This cannot be undone.
          </DialogContentText>
          {deleteGameError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDeleteGameError("")}>{deleteGameError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteGameDialog({ open: false, game: null })} disabled={deleteGameLoading}>Back</Button>
          <Button variant="contained" color="error" onClick={handleDeleteGame} disabled={deleteGameLoading}>
            {deleteGameLoading ? "Deleting…" : "Delete Game"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkEditDialog.open} maxWidth="xs" fullWidth onClose={() => !bulkEditSaving && setBulkEditDialog({ open: false })}>
        <DialogTitle>Bulk edit {selectedGameIds.length} game{selectedGameIds.length !== 1 ? "s" : ""}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Leave a field unchanged to keep each game's current value.
            </Typography>
            <DateTimePicker
              label="Date & time (optional)"
              value={bulkEditStartsAt}
              onChange={(v) => setBulkEditStartsAt(v)}
              minDateTime={dayjs()}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <FormControl fullWidth>
              <InputLabel id="bulk-edit-rink-label">Rink</InputLabel>
              <Select
                labelId="bulk-edit-rink-label"
                label="Rink"
                value={bulkEditRinkId}
                onChange={(e) => setBulkEditRinkId(e.target.value)}
              >
                <MenuItem value="__unchanged__"><em>— Keep unchanged —</em></MenuItem>
                <MenuItem value=""><em>No rink assigned</em></MenuItem>
                {existingRinks.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}{r.address ? ` — ${r.address}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Duration (minutes)"
              type="number"
              inputProps={{ min: 1 }}
              value={bulkEditDurationMinutes}
              onChange={(e) => setBulkEditDurationMinutes(e.target.value)}
              placeholder="Leave blank to keep unchanged"
              fullWidth
            />
            {bulkEditError && <Alert severity="error" onClose={() => setBulkEditError("")}>{bulkEditError}</Alert>}
            {bulkEditFailures.length > 0 && (
              <Alert severity="warning" onClose={() => setBulkEditFailures([])}>
                <strong>{bulkEditFailures.length} game{bulkEditFailures.length > 1 ? "s" : ""} could not be updated:</strong>
                <ul style={{ margin: "4px 0 0", paddingLeft: "1.2em" }}>
                  {bulkEditFailures.map((f, i) => (
                    <li key={i}><strong>{f.label}</strong> — {f.reason}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditDialog({ open: false })} disabled={bulkEditSaving}>
            {bulkEditFailures.length > 0 ? "Close" : "Cancel"}
          </Button>
          <Button variant="contained" onClick={handleSaveBulkEdit} disabled={bulkEditSaving}>
            {bulkEditSaving ? "Saving…" : bulkEditFailures.length > 0 ? "Retry" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteDialog.open} maxWidth="xs" fullWidth onClose={() => !bulkDeleteLoading && setBulkDeleteDialog({ open: false })}>
        <DialogTitle>Bulk delete games?</DialogTitle>
        <DialogContent>
          {(() => {
            const deletable = leagueGames.filter(
              (g) => selectedGameIds.includes(g.id) && (g.skaterSpotsFilled ?? 0) === 0 && (g.goalieSpotsFilled ?? 0) === 0
            );
            const skipped = selectedGameIds.length - deletable.length;
            return (
              <Stack spacing={1}>
                <DialogContentText>
                  {deletable.length} game(s) will be permanently deleted. This cannot be undone.
                </DialogContentText>
                {skipped > 0 && (
                  <Alert severity="warning">
                    {skipped} game(s) with existing registrations will be skipped. Use the individual Cancel button for those.
                  </Alert>
                )}
              </Stack>
            );
          })()}
          {bulkDeleteError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setBulkDeleteError("")}>{bulkDeleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog({ open: false })} disabled={bulkDeleteLoading}>Back</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={bulkDeleteLoading || leagueGames.filter((g) => selectedGameIds.includes(g.id) && (g.skaterSpotsFilled ?? 0) === 0 && (g.goalieSpotsFilled ?? 0) === 0).length === 0}
          >
            {bulkDeleteLoading ? "Deleting…" : "Delete Games"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rinkConflictDialog.open} maxWidth="sm" fullWidth onClose={() => setRinkConflictDialog(s => ({ ...s, open: false }))}>
        <DialogTitle>Rink has existing games</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>{rinkConflictDialog.rinkName}</strong> is assigned to{" "}
            {rinkConflictDialog.affectedGames.length} game(s). Choose a replacement rink — the reassignment
            and player email notifications will be sent when you click <strong>Update</strong>.
          </DialogContentText>
          {rinkDeleteError ? <Alert severity="error" sx={{ mb: 2 }}>{rinkDeleteError}</Alert> : null}
          <FormControl fullWidth>
            <InputLabel>Replacement rink</InputLabel>
            <Select
              label="Replacement rink"
              value={rinkConflictDialog.replacementRinkId}
              onChange={(e) => setRinkConflictDialog(s => ({ ...s, replacementRinkId: e.target.value }))}
            >
              {existingRinks.filter(r => r.id !== rinkConflictDialog.rinkId).map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}{r.address ? ` — ${r.address}` : ""}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRinkConflictDialog(s => ({ ...s, open: false }))}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!rinkConflictDialog.replacementRinkId}
            onClick={handleConfirmRinkReassign}
          >
            Confirm Replacement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price override confirmation dialog */}
      <Dialog open={priceOverrideDialogOpen} onClose={() => setPriceOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unlock pricing?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              One or more games already have registered participants who were quoted the current pricing. Changing the player cost, goalie cost, or payment type will <strong>not</strong> automatically update what existing registrants owe — you will need to handle any billing adjustments manually.
            </DialogContentText>
            <FormControlLabel
              control={
                <Checkbox
                  checked={priceOverrideAcknowledged}
                  onChange={(e) => setPriceOverrideAcknowledged(e.target.checked)}
                />
              }
              label="I understand that existing registrations used the previous price and I will handle any adjustments."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceOverrideDialogOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={!priceOverrideAcknowledged}
            onClick={() => {
              setPriceOverrideEnabled(true);
              setPriceOverrideDialogOpen(false);
            }}
          >
            Unlock pricing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price history dialog */}
      <Dialog open={priceHistoryDialogOpen} onClose={() => setPriceHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Price history</DialogTitle>
        <DialogContent>
          {priceHistoryLoading ? (
            <LinearProgress sx={{ mt: 1 }} />
          ) : priceHistory.length === 0 ? (
            <DialogContentText>No price changes recorded yet. History is saved each time pricing is updated.</DialogContentText>
          ) : (
            <Stack spacing={0} divider={<Divider />}>
              {priceHistory.map((entry) => (
                <Box key={entry.id} sx={{ py: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.changedAt).toLocaleString()}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={entry.gamePaymentType === "FULL_LEAGUE" ? "Full league" : "Drop-in"} variant="outlined" />
                    <Chip size="small" label={`Player: $${(entry.playerCost / 100).toFixed(2)}`} variant="outlined" />
                    <Chip size="small" label={`Goalie: $${(entry.goalieCost / 100).toFixed(2)}`} variant="outlined" />
                    {entry.iceTimeCost != null && (
                      <Chip size="small" label={`Ice time: $${(entry.iceTimeCost / 100).toFixed(2)}`} variant="outlined" />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
    </LocalizationProvider>
  );
}
